import React from 'react';
import { connect } from 'react-redux';
import isNumeric from 'validator/es/lib/isNumeric';
import {
  clientError, firstError, responseErrors, invalidUrlCharacters,
} from '@/utils/errors';
import { hasMinimumLength } from '@/utils/resultsHelper';
import urlDenyCharacters from '@/constants/validation';
import fields from '@/constants/fields';
import { states } from '@/constants/lifecycle';
import ForwardButton from '@/components/buttons/ForwardButton';
import SaveButton from '@/components/buttons/SaveButton';
import DatasourceTestButton, { testDatasourceValidators } from '../datasource/components/DatasourceTestButton';
import { mapStateToProps, mapDispatchToProps } from '../components/DatasetWizardActionsContainer';
import { catalogueTitleErrors } from '../catalogue/helper';
import DatasetWizardCancelButton from './DatasetWizardCancelButton';
import { datasourceSupportsTesting } from '../datasource/helper';

const DatasetTypeActions = ({
  canChangeDatasource,
  category,
  code,
  compact,
  confirmTypeChange,
  dataset,
  datasetCode,
  datasourceSettings,
  isDirty,
  isNew,
  previousTitle,
  readErrors,
  saveErrors,
  saveStatus,
  template,
  testSettings,
  title,
  updateDatasourceSettings,
  updateErrors,
  updateStep,
}) => {
  const validateStep = () => {
    const errors = catalogueTitleErrors(dataset);
    if (isNew && datasetCode && (isNumeric(datasetCode) || invalidUrlCharacters(datasetCode))) {
      const error = clientError(
        { entity: 'catalogue' },
        'save.dataset.validation.code.title',
        'validation.invalid.code',
        [fields.CODE],
      );
      error.translation.on = true;
      error.translation.messageArgs = { characters: urlDenyCharacters };
      errors.push(error);
    }
    return errors;
  };

  const validateDatasource = () => {
    const validator = isDirty.datasource ? testDatasourceValidators[datasourceSettings.copy.type] : null;
    const validationErrors = validator ? validator(datasourceSettings.copy) : [];

    updateDatasourceSettings(category, code, { saveErrors: validationErrors });
    return validationErrors;
  };

  const goForward = () => {
    const validationErrors = validateStep();
    updateErrors(category, code, [...validationErrors, ...responseErrors(saveErrors)]);

    if (validationErrors.length === 0 && validateDatasource().length === 0) {
      updateStep(category, code, 2, template);
    }
  };

  const onSaveClick = () => {
    const validationErrors = validateStep();
    updateErrors(category, code, validationErrors);

    if (validationErrors.length === 0 && validateDatasource().length === 0) {
      confirmTypeChange(category, code, template, title, previousTitle, isDirty.template, isDirty.datasource);
    }
  };

  const requiresSave = !isNew && (isDirty.template || isDirty.datasource);
  const testValidator = canChangeDatasource ? testDatasourceValidators[datasourceSettings.copy.type] : null;
  const disableTest = !datasourceSupportsTesting(datasourceSettings.copy, testValidator);
  const testStatus = testValidator ? datasourceSettings.copy.testStatus : states.NONE;
  const minimumDataEntryMet = hasMinimumLength(title) && hasMinimumLength(template.code)
      && !firstError(readErrors) && testStatus !== states.PENDING;

  return (
    <div className="aridhia-dataset-wizard-actions aridhia-tab-actions">
      {testValidator && (
        <div className={compact ? 'aridhia-tab-actions-button-compact' : 'aridhia-tab-actions-button'}>
          <DatasourceTestButton
            connection={datasourceSettings.copy.connection}
            disabled={disableTest}
            iconOnly={compact}
            onClick={() => testSettings(category, code)}
            testStatus={datasourceSettings.copy.testStatus}
          />
        </div>
      )}
      <div className="actions">
        <DatasetWizardCancelButton category={category} code={code} />
        {!requiresSave && (<ForwardButton disabled={!minimumDataEntryMet} iconOnly={compact} onClick={goForward} />)}
        {requiresSave && (
          <SaveButton
            iconName="chevron right"
            iconOnly={compact}
            inProgress={saveStatus === states.PENDING}
            messageId="actions.save.proceed"
            minimumDataEntryMet={minimumDataEntryMet}
            onClick={onSaveClick}
          />
        )}
      </div>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(DatasetTypeActions);
